import { useParams, Link } from "react-router-dom";
import { useState, useRef, useEffect, useCallback } from "react";
import posthog from "posthog-js";
import {
  getTemplateById,
  TemplateLayer,
  LayerPosition,
} from "../builderTemplates";
import { SEO } from "../components/SEO";
import { BuilderSidebar } from "../components/BuilderSidebar";
import { buildAnimatedGif, findGifLayerIndex } from "../gifExport";

interface LayerState extends TemplateLayer {
  imageData: string | null;
}

type ResizeDirection = 'nw' | 'ne' | 'se' | 'sw';

interface PointerPosition {
  clientX: number;
  clientY: number;
}

export function EditorPage() {
  const { id } = useParams<{ id: string }>();
  const template = id ? getTemplateById(id) : undefined;

  const [layers, setLayers] = useState<LayerState[]>([]);
  const [activeLayerIndex, setActiveLayerIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<ResizeDirection | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [hogName, setHogName] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewIsGif, setPreviewIsGif] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState<number>(500);
  const [canvasHeight, setCanvasHeight] = useState<number>(500);
  const [widthInput, setWidthInput] = useState<string>("500");
  const [heightInput, setHeightInput] = useState<string>("500");
  const displayScale = 3;
  const canvasRef = useRef<HTMLDivElement>(null);
  const exportCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (template) {
      const initialLayers: LayerState[] = template.layers.map((layer) => ({
        ...layer,
        imageData: null,
      }));
      setLayers(initialLayers);
      setActiveLayerIndex(template.activeLayerIndex);
      setCanvasWidth(template.canvasWidth);
      setCanvasHeight(template.canvasHeight);
      setWidthInput(String(template.canvasWidth));
      setHeightInput(String(template.canvasHeight));
    }
  }, [template]);

  const layerHasNoPosition = (layer: LayerState): boolean =>
    layer.position.width === 0 && layer.position.height === 0;

  const loadImageWithPosition = useCallback(
    (imageData: string, layerIndex: number): Promise<void> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          setLayers((prev) =>
            prev.map((layer, idx) => {
              if (idx !== layerIndex) return layer;
              if (layerHasNoPosition(layer)) {
                return {
                  ...layer,
                  imageData,
                  position: {
                    x: canvasWidth / 4,
                    y: canvasHeight / 4,
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                  },
                };
              }
              return { ...layer, imageData };
            })
          );
          resolve();
        };
        img.onerror = () => {
          setLayers((prev) =>
            prev.map((layer, idx) =>
              idx === layerIndex ? { ...layer, imageData } : layer
            )
          );
          resolve();
        };
        img.src = imageData;
      });
    },
    [canvasWidth, canvasHeight]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
              const imageData = event.target?.result as string;
              loadImageWithPosition(imageData, activeLayerIndex);
            };
            reader.readAsDataURL(file);
            posthog.capture("image_pasted", { template_id: template?.id, layer_index: activeLayerIndex });
          }
          break;
        }
      }
    },
    [activeLayerIndex, loadImageWithPosition, template?.id]
  );

  useEffect(() => {
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [handlePaste]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;

      if (e.key === "Escape") {
        setActiveLayerIndex(-1);
        return;
      }

      if (e.key === "Delete" || e.key === "Backspace") {
        const layer = layers[activeLayerIndex];
        if (layer && !layer.file && layer.imageData) {
          clearLayerImage(activeLayerIndex);
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [layers, activeLayerIndex]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, targetLayerIndex?: number) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;

    const layerIndex = targetLayerIndex ?? activeLayerIndex;
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageData = event.target?.result as string;
      loadImageWithPosition(imageData, layerIndex);
    };
    reader.readAsDataURL(file);
    input.value = "";
    posthog.capture("image_uploaded", { template_id: template?.id, layer_index: layerIndex });
  };

  const updateLayerPosition = (
    index: number,
    updates: Partial<LayerPosition>
  ) => {
    setLayers((prev) => {
      const layer = prev[index];
      if (!layer) return prev;
      const next = { ...layer.position, ...updates };
      const current = layer.position;
      if (
        next.x === current.x &&
        next.y === current.y &&
        next.width === current.width &&
        next.height === current.height
      ) {
        return prev;
      }
      return prev.map((l, idx) =>
        idx === index ? { ...l, position: next } : l
      );
    });
  };

  const clearLayerImage = (index: number) => {
    setLayers((prev) =>
      prev.map((layer, idx) =>
        idx === index ? { ...layer, imageData: null } : layer
      )
    );
  };

  const isUserEditableLayer = (layer: LayerState) => !layer.file;

  const handleMouseDown = (e: React.MouseEvent) => {
    const layer = layers[activeLayerIndex];
    if (!layer) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX / displayScale - layer.position.x,
      y: e.clientY / displayScale - layer.position.y,
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const layer = layers[activeLayerIndex];
    if (!layer) return;

    const touch = e.touches[0];
    if (!touch) return;

    setIsDragging(true);
    setDragStart({
      x: touch.clientX / displayScale - layer.position.x,
      y: touch.clientY / displayScale - layer.position.y,
    });
  };

  const createTouchToMouseHandler = (handler: (e: PointerPosition) => void) => {
    return (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      if (touch) {
        handler({ clientX: touch.clientX, clientY: touch.clientY });
      }
    };
  };

  const handleResizeStart = (e: React.MouseEvent | PointerPosition, direction: ResizeDirection) => {
    if ('stopPropagation' in e) {
      e.stopPropagation();
    }
    const layer = layers[activeLayerIndex];
    if (!layer) return;

    setIsResizing(true);
    setResizeDirection(direction);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialSize({
      width: layer.position.width,
      height: layer.position.height,
    });
  };

  const handleMove = (clientX: number, clientY: number) => {
    if (!isDragging && !isResizing) return;

    const layer = layers[activeLayerIndex];
    if (!layer) return;

    if (isDragging) {
      const newX = clientX / displayScale - dragStart.x;
      const newY = clientY / displayScale - dragStart.y;
      updateLayerPosition(activeLayerIndex, { x: newX, y: newY });
    } else if (isResizing) {
      const deltaX = (clientX - dragStart.x) / displayScale;
      const deltaY = (clientY - dragStart.y) / displayScale;
      
      let updates: Partial<LayerPosition> = {};

      switch (resizeDirection) {
        case 'se':
          const seDelta = Math.max(deltaX, deltaY);
          updates = {
            width: Math.max(10, initialSize.width + seDelta),
            height: Math.max(10, initialSize.height + seDelta),
          };
          break;
        case 'nw':
          const nwDelta = Math.max(-deltaX, -deltaY);
          const nwNewSize = Math.max(10, initialSize.width + nwDelta);
          updates = {
            x: layer.position.x - (nwNewSize - layer.position.width),
            y: layer.position.y - (nwNewSize - layer.position.height),
            width: nwNewSize,
            height: nwNewSize,
          };
          break;
        case 'ne':
          const neDelta = Math.max(deltaX, -deltaY);
          const neNewSize = Math.max(10, initialSize.width + neDelta);
          updates = {
            y: layer.position.y - (neNewSize - layer.position.height),
            width: neNewSize,
            height: neNewSize,
          };
          break;
        case 'sw':
          const swDelta = Math.max(-deltaX, deltaY);
          const swNewSize = Math.max(10, initialSize.width + swDelta);
          updates = {
            x: layer.position.x - (swNewSize - layer.position.width),
            width: swNewSize,
            height: swNewSize,
          };
          break;
      }

      updateLayerPosition(activeLayerIndex, updates);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    handleMove(e.clientX, e.clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      handleMove(touch.clientX, touch.clientY);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  };

  const fillCanvasWithFrameLayers = (width: number, height: number) => {
    setLayers((prev) =>
      prev.map((layer) =>
        layer.file
          ? { ...layer, position: { x: 0, y: 0, width, height } }
          : layer
      )
    );
  };

  const handleWidthBlur = () => {
    const value = parseInt(widthInput, 10);
    const validated = isNaN(value) ? 100 : Math.max(100, Math.min(2000, value));
    setCanvasWidth(validated);
    setWidthInput(String(validated));
    fillCanvasWithFrameLayers(validated, canvasHeight);
  };

  const handleHeightBlur = () => {
    const value = parseInt(heightInput, 10);
    const validated = isNaN(value) ? 100 : Math.max(100, Math.min(2000, value));
    setCanvasHeight(validated);
    setHeightInput(String(validated));
    fillCanvasWithFrameLayers(canvasWidth, validated);
  };

  const buildExportLayers = () =>
    layers.map((layer) => ({
      src: layer.imageData || layer.file || null,
      position: layer.position,
      visible: layer.visible,
    }));

  const generatePreview = async (): Promise<{ url: string; isGif: boolean } | null> => {
    const exportLayers = buildExportLayers();
    const gifIndex = findGifLayerIndex(exportLayers);

    if (gifIndex !== -1) {
      const blob = await buildAnimatedGif({
        layers: exportLayers,
        gifLayerIndex: gifIndex,
        canvasWidth,
        canvasHeight,
        workerScript: `${process.env.PUBLIC_URL || ""}/gif.worker.js`,
      });
      return { url: URL.createObjectURL(blob), isGif: true };
    }

    const canvas = exportCanvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const drawable = exportLayers.filter(
      (layer): layer is typeof layer & { src: string } => layer.visible && !!layer.src
    );

    const images = await Promise.all(
      drawable.map(({ src }) =>
        new Promise<HTMLImageElement | null>((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
          img.src = src;
        })
      )
    );

    drawable.forEach((layer, i) => {
      const img = images[i];
      if (!img) return;
      ctx.drawImage(
        img,
        layer.position.x,
        layer.position.y,
        layer.position.width,
        layer.position.height
      );
    });

    return { url: canvas.toDataURL("image/png"), isGif: false };
  };

  const openDownloadModal = async () => {
    if (previewUrl && previewIsGif) URL.revokeObjectURL(previewUrl);
    setIsExporting(true);
    setShowDownloadModal(true);
    setHogName(`${template?.id || "hog"}-`);
    posthog.capture("download_modal_opened", { template_id: template?.id });
    try {
      const result = await generatePreview();
      setPreviewUrl(result?.url ?? null);
      setPreviewIsGif(result?.isGif ?? false);
    } finally {
      setIsExporting(false);
    }
  };

  const triggerDownload = (url: string, filename: string) => {
    const link = document.createElement("a");
    link.download = filename;
    link.href = url;
    link.click();
  };

  const exportExtension = () => (previewIsGif ? "gif" : "png");

  const handleDownload = () => {
    if (!previewUrl) return;
    const filename = `${hogName || "hog"}.${exportExtension()}`;
    triggerDownload(previewUrl, filename);
    posthog.capture("hogmoji_downloaded", { template_id: template?.id, file_name: filename });
  };

  const handleCopyToClipboard = async () => {
    if (!previewUrl) return;
    const mime = previewIsGif ? "image/gif" : "image/png";
    try {
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([new ClipboardItem({ [mime]: blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      posthog.capture("hogmoji_copied", { template_id: template?.id });
    } catch (err) {
      triggerDownload(previewUrl, `${hogName || "hog"}.${exportExtension()}`);
    }
  };

  if (!template) {
    return (
      <div className="flex flex-col items-center justify-center flex-grow px-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          Template not found
        </h1>
        <Link to="/" className="text-hog-500 hover:underline">
          Back to templates
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${template.name} - create custom hogmoji`}
        description={`Use the ${template.name} template to create your own custom PostHog hedgehog emoji. Design, customize, and export your perfect hogmoji.`}
        keywords="hogmoji, posthog, hedgehog, builder, create, custom, emoji, design, editor"
        url={`https://hogmoji.fun/${id}`}
      />
      <div className="flex flex-grow h-full relative">
        <BuilderSidebar
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          widthInput={widthInput}
          heightInput={heightInput}
          onWidthChange={setWidthInput}
          onHeightChange={setHeightInput}
          onWidthBlur={handleWidthBlur}
          onHeightBlur={handleHeightBlur}
          layers={layers}
          activeLayerIndex={activeLayerIndex}
          onLayerClick={setActiveLayerIndex}
          onFileUpload={handleFileUpload}
          onClearLayerImage={clearLayerImage}
          isUserEditableLayer={isUserEditableLayer}
          onDownload={openDownloadModal}
        />

        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 md:hidden flex gap-3" role="group" aria-label="Layer selection and actions">
          {layers.map((layer, idx) => {
            const imgSrc = layer.imageData || layer.file;
            const isActive = idx === activeLayerIndex;
            const isEditable = isUserEditableLayer(layer);
            const isEmptyEditableLayer = isEditable && !layer.imageData;
            
            if (isEmptyEditableLayer) {
              return (
                <label
                  key={layer.id}
                  onClick={() => setActiveLayerIndex(idx)}
                  className={`w-14 h-14 rounded-lg shadow-lg flex items-center justify-center transition-all cursor-pointer ${
                    isActive
                      ? "bg-hog-200 ring-2 ring-hog-300"
                      : "bg-white hover:bg-gray-50"
                  }`}
                  aria-label={`${layer.name} - Add image`}
                >
                  <div className="w-10 h-10 flex items-center justify-center overflow-hidden rounded">
                    <svg
                      className="w-6 h-6 text-hog-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                      handleFileUpload(e, idx);
                      setActiveLayerIndex(idx);
                    }}
                  />
                </label>
              );
            }
            
            return (
              <div key={layer.id} className="relative">
                <button
                  onClick={() => setActiveLayerIndex(idx)}
                  className={`w-14 h-14 rounded-lg shadow-lg flex items-center justify-center transition-all ${
                    isActive
                      ? "bg-hog-200 ring-2 ring-hog-300"
                      : "bg-white hover:bg-gray-50"
                  }`}
                  aria-label={layer.name}
                  aria-pressed={isActive}
                >
                  <div className="w-10 h-10 flex items-center justify-center overflow-hidden rounded">
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={layer.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg
                        className={`w-6 h-6 ${isActive ? "text-hog-600" : "text-gray-400"}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    )}
                  </div>
                </button>
                {isEditable && layer.imageData && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearLayerImage(idx);
                    }}
                    className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-md flex items-center justify-center transition-colors"
                    aria-label="Clear image"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
          <button
            onClick={openDownloadModal}
            className="w-14 h-14 rounded-lg shadow-lg bg-hog-500 hover:bg-hog-600 text-white flex items-center justify-center transition-all"
            aria-label="Download"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>
        </div>

        <main className="flex-grow bg-gray-200 flex items-center justify-center overflow-auto p-4">
          <div
            ref={canvasRef}
            className="relative bg-white shadow-xl overflow-hidden"
            style={{
              width: canvasWidth,
              height: canvasHeight,
              transform: `scale(${displayScale})`,
              transformOrigin: "center center",
              imageRendering: "pixelated",
              backgroundImage:
                "linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)",
              backgroundSize: "8px 8px",
              backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
              touchAction: "none",
            }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleMouseUp}
        >
          {layers.map((layer, idx) => {
            if (!layer.visible) return null;

            const imgSrc = layer.imageData || layer.file;
            if (!imgSrc) return null;

            const isActive = idx === activeLayerIndex;

            return (
              <div
                key={layer.id}
                className={`absolute cursor-move ${isActive ? "ring-1 ring-hog-500" : ""}`}
                style={{
                  left: layer.position.x,
                  top: layer.position.y,
                  width: layer.position.width,
                  height: layer.position.height,
                  zIndex: idx,
                  pointerEvents: isActive ? "auto" : "none",
                  touchAction: isActive ? "none" : "auto",
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
              >
                <img
                  src={imgSrc}
                  alt={layer.name}
                  className="w-full h-full object-contain pointer-events-none select-none"
                  style={{ imageRendering: "pixelated" }}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                />
                {isActive && (
                  <>
                    <div
                      className="absolute top-0 left-0 cursor-nw-resize -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
                      style={{ width: 24, height: 24, touchAction: "none" }}
                      onMouseDown={(e) => handleResizeStart(e, 'nw')}
                      onTouchStart={createTouchToMouseHandler((e) => handleResizeStart(e, 'nw'))}
                    >
                      <div className="bg-white border-2 border-hog-500 rounded-sm" style={{ width: 8, height: 8 }} />
                    </div>
                    <div
                      className="absolute top-0 right-0 cursor-ne-resize translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
                      style={{ width: 24, height: 24, touchAction: "none" }}
                      onMouseDown={(e) => handleResizeStart(e, 'ne')}
                      onTouchStart={createTouchToMouseHandler((e) => handleResizeStart(e, 'ne'))}
                    >
                      <div className="bg-white border-2 border-hog-500 rounded-sm" style={{ width: 8, height: 8 }} />
                    </div>
                    <div
                      className="absolute bottom-0 right-0 cursor-se-resize translate-x-1/2 translate-y-1/2 flex items-center justify-center"
                      style={{ width: 24, height: 24, touchAction: "none" }}
                      onMouseDown={(e) => handleResizeStart(e, 'se')}
                      onTouchStart={createTouchToMouseHandler((e) => handleResizeStart(e, 'se'))}
                    >
                      <div className="bg-white border-2 border-hog-500 rounded-sm" style={{ width: 8, height: 8 }} />
                    </div>
                    <div
                      className="absolute bottom-0 left-0 cursor-sw-resize -translate-x-1/2 translate-y-1/2 flex items-center justify-center"
                      style={{ width: 24, height: 24, touchAction: "none" }}
                      onMouseDown={(e) => handleResizeStart(e, 'sw')}
                      onTouchStart={createTouchToMouseHandler((e) => handleResizeStart(e, 'sw'))}
                    >
                      <div className="bg-white border-2 border-hog-500 rounded-sm" style={{ width: 8, height: 8 }} />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <canvas ref={exportCanvasRef} className="hidden" />
      </main>
      </div>

      {showDownloadModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Save your hogmoji</h2>
              <button
                onClick={() => setShowDownloadModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <div className="mb-4 bg-gray-100 rounded-lg p-4 flex items-center justify-center min-h-[128px]">
                {isExporting ? (
                  <span className="text-sm text-gray-500">Rendering…</span>
                ) : previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-w-full max-h-48 object-contain"
                  />
                ) : null}
              </div>
              {previewIsGif && !isExporting && (
                <p className="mb-3 text-xs text-gray-500">Exporting as animated GIF</p>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={hogName}
                  onChange={(e) => setHogName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hog-500 focus:border-hog-500 outline-none"
                  placeholder="hog-custom"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleDownload}
                  disabled={isExporting || !previewUrl}
                  className="flex-1 py-2 px-4 bg-hog-500 text-white rounded-lg font-medium hover:bg-hog-600 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                <button
                  onClick={handleCopyToClipboard}
                  disabled={isExporting || !previewUrl}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${
                    copied
                      ? "bg-hog-100 text-hog-700"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {copied ? (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
